/**
 * El registro de faltantes del diccionario (encargo 49): lo que el diccionario
 * no tenía cuando un alumno tocó una palabra, con su historial de estados.
 *
 *   GET  /admin/lexicon-missing            — listado con filtros + conteos por estado.
 *   GET  /admin/lexicon-missing/:id        — el candidato y su historial.
 *   POST /admin/lexicon-missing/:id/reject — rechazar con motivo.
 *   POST /admin/lexicon-missing/:id/reopen — volver a «pendiente».
 *   POST /admin/lexicon-missing/:id/approve — el OK humano (sólo tras el juez).
 */
import { get, postJsonAdmin } from './_core.js';

export const MISSING_STATUSES = [
  'pending',
  'needs_human',
  'rejected',
  'in_review',
  'judge_approved',
  'approved',
  'exported',
  'published',
  'not_included',
] as const;
export type MissingStatus = (typeof MISSING_STATUSES)[number];

export interface MissingCandidate {
  id: string;
  kind: 'meaning' | 'word';
  word: string;
  surface: string;
  context: string;
  origin: 'coach' | 'learner' | 'definition' | 'example' | 'example-es';
  status: MissingStatus;
  reasonCode: string | null;
  reason: string | null;
  hits: number;
  entryVersion: string | null;
  selector: string | null;
  writerModel: string | null;
  judgeModel: string | null;
  caseId: string | null;
  createdAt: string;
  lastSeenAt: string;
  statusAt: string;
  sentence: string;
  originRef: string | null;
  filters: FilterStep[];
  draft: MissingDraft | null;
  review: { rounds: ReviewRound[] } | null;
  students: number;
  approvedBy: string | null;
  approvedAt: string | null;
  publishedAt: string | null;
}

/** Un paso del recorrido de filtros previos. */
export interface FilterStep {
  filtro: string;
  resultado: 'pasa' | 'frena' | 'dudoso' | 'no_aplica';
  detalle: string;
}

/** El significado provisorio de una palabra nueva (escritor + juez). */
export interface MissingDraft {
  translation: string;
  clarification: string | null;
  pos: string;
  definition: string;
  example: string;
  exampleEs: string;
}

export interface ReviewRound {
  draft: (MissingDraft & { notes?: string | null }) | null;
  verdict: { approved: boolean; problems: string[] } | null;
  writer: { model: string; inputTokens: number; outputTokens: number; latencyMs: number } | null;
  judge: { model: string; inputTokens: number; outputTokens: number; latencyMs: number } | null;
}

export interface MissingEvent {
  id: string;
  at: string;
  from: MissingStatus | null;
  to: MissingStatus;
  actor: string;
  reasonCode: string | null;
  reason: string | null;
  detail: unknown;
}

export interface MissingFilters {
  status?: MissingStatus;
  kind?: 'meaning' | 'word';
  origin?: MissingCandidate['origin'];
  q?: string;
  from?: string;
  to?: string;
  cursor?: string;
}

export interface MissingPage {
  candidates: MissingCandidate[];
  counts: Partial<Record<MissingStatus, number>>;
  nextCursor: string | null;
}

export async function fetchMissingCandidates(f: MissingFilters): Promise<MissingPage> {
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(f)) if (v) params.set(k, String(v));
  const qs = params.toString();
  const res = await get<{ candidates: MissingCandidate[]; counts: MissingPage['counts'] }>(
    `/admin/lexicon-missing${qs ? `?${qs}` : ''}`,
  );
  return { ...res.data, nextCursor: res.meta?.nextCursor ?? null };
}

export async function fetchMissingCandidate(
  id: string,
): Promise<{ candidate: MissingCandidate; events: MissingEvent[] }> {
  return (
    await get<{ candidate: MissingCandidate; events: MissingEvent[] }>(
      `/admin/lexicon-missing/${encodeURIComponent(id)}`,
    )
  ).data;
}

export async function syncPublishedMissing(): Promise<{ published: number; notIncluded: number }> {
  return postJsonAdmin('/admin/lexicon-missing/sync-published', {});
}

export function actOnMissingCandidate(
  id: string,
  action: 'reject' | 'reopen' | 'approve',
  reason?: string,
): Promise<{ id: string; from: MissingStatus; to: MissingStatus }> {
  return postJsonAdmin(
    `/admin/lexicon-missing/${encodeURIComponent(id)}/${action}`,
    reason ? { reason } : {},
  );
}
